import {
  Add,
  AttachMoney,
  CalendarMonthOutlined,
  Check,
  Close,
  NotificationsNoneOutlined,
  ScheduleOutlined,
  Search,
  TableRowsOutlined,
  ViewKanbanOutlined,
} from "@mui/icons-material";
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PageActions, PageBreadcrumbs, PageTitle } from "pages/Layouts/components/PageTitleContainer";
import { FullPagePaperLayout } from "pages/Layouts/FullPagePaperLayout";
import { PagePaperPreamble } from "pages/Layouts/components/PagePaperPreamble";
import { ChangeEvent, MouseEvent, ReactNode, UIEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { format, formatDistanceToNow } from "date-fns";
import { debounce } from "lodash";
import useLogisticsEvents from "hooks/useLogisticsEvents";
import { LogisticsEventType } from "types/logisticsEvents";
import LogisticsTicketsTableView from "./LogisticsTicketsTableView";
import LogisticsPickupPipelineView from "./Pipeline/LogisticsPickupPipelineView";
import LogisticsTicketsCalendarView from "./Calendar/LogisticsTicketsCalendarView";
import { mapLogisticsTicketsToPipelinePickups } from "./Pipeline/mapLogisticsTicketToPipelinePickup";
import useLogisticsTickets from "hooks/useLogisticsTickets";
import useVendors from "hooks/useVendors";
import { logisticsTeam } from "constants/logisticsTeam";
import {
  ActionFilter,
  matchesCoordinatorFilter,
  matchesNeedsActionFilter,
  matchesVendorFilter,
} from "./matchesCoordinatorFilter";
import {
  LogisticsPickupsView,
  LogisticsPlatformFilters,
  logisticsPlatformFiltersToSearchParams,
  readLogisticsPlatformFilters,
  writeLogisticsPlatformFilters,
} from "./logisticsPlatformFilters";

const COORDINATOR_FILTER_OPTIONS = [
  { id: "all", name: "All coordinators" },
  ...logisticsTeam.map(([id, name]) => ({ id, name })),
  { id: "unassigned", name: "Unassigned" },
];

const logisticsEventTypesData: Record<
  LogisticsEventType,
  { icon: ReactNode; iconColor: string; iconBackgroundColor: string }
> = {
  pickup_requested: {
    icon: <Add />,
    iconColor: "#1D64C8",
    iconBackgroundColor: "#E4EEFF",
  },
  vendor_accepted: {
    icon: <Check />,
    iconColor: "#07834A",
    iconBackgroundColor: "#DDF9EA",
  },
  customer_confirmed: {
    icon: <Check />,
    iconColor: "#07834A",
    iconBackgroundColor: "#DDF9EA",
  },
  quote_received: {
    icon: <AttachMoney />,
    iconColor: "#6931C8",
    iconBackgroundColor: "#EEE7FF",
  },
  customer_new_proposed_time: {
    icon: <ScheduleOutlined />,
    iconColor: "#956500",
    iconBackgroundColor: "#FFF4C7",
  },
  vendor_declined: {
    icon: <Close />,
    iconColor: "#D32F2F",
    iconBackgroundColor: "#FFEBEE",
  },
  pickup_completed: {
    icon: <Check />,
    iconColor: "#07834A",
    iconBackgroundColor: "#DDF9EA",
  },
  delivery_verified: {
    icon: <Check />,
    iconColor: "#07834A",
    iconBackgroundColor: "#DDF9EA",
  },
};

const NotificationsAction = () => {
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const {
    logisticsEvents,
    unreadCount,
    loading: isLoading,
    hasMore,
    fetchMoreLogisticsEvents,
    markAsRead,
    markAllAsRead,
  } = useLogisticsEvents();
  const loadLockedRef = useRef(false);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const reachedBottom = scrollHeight - scrollTop - clientHeight < 40;

    // Unlock only after the user scrolls away from the bottom, so appending
    // new rows (which keeps us near the bottom) does not chain-load every page.
    if (!reachedBottom) {
      loadLockedRef.current = false;
      return;
    }

    if (loadLockedRef.current || !hasMore || isLoading) return;

    loadLockedRef.current = true;
    fetchMoreLogisticsEvents();
  };

  return (
    <>
      <IconButton
        aria-label={`${unreadCount} unread notifications`}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorElement)}
        onClick={handleOpen}
        sx={{ border: 1, borderColor: "divider", borderRadius: 1, width: 40, height: 40 }}
      >
        <Badge badgeContent={unreadCount} color="secondary">
          <NotificationsNoneOutlined />
        </Badge>
      </IconButton>
      <Popover
        open={Boolean(anchorElement)}
        anchorEl={anchorElement}
        onClose={() => setAnchorElement(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 360,
            maxWidth: "calc(100vw - 32px)",
            borderRadius: 1.5,
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Notifications{" "}
            <Typography component="span" color="text.secondary">
              · {unreadCount} unread
            </Typography>
          </Typography>
          <Button
            size="small"
            disabled={unreadCount === 0}
            onClick={() => markAllAsRead()}
            sx={{ textTransform: "none", whiteSpace: "nowrap" }}
          >
            Mark all as read
          </Button>
        </Box>
        <Divider />
        <Box onScroll={handleScroll} sx={{ maxHeight: 448, overflowY: "scroll" }}>
          {logisticsEvents.map((notification, index) => {
            return (
              <Box key={notification.id} onClick={() => markAsRead(notification.id)}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    borderLeft: 3,
                    borderLeftColor: notification.read ? "transparent" : "primary.main",
                    backgroundColor: notification.read ? "background.paper" : "#f6fafc",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#e6f0f5",
                    },
                  }}
                >
                  <Box
                    sx={{
                      mt: 0.25,
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      color: logisticsEventTypesData[notification.eventType].iconColor,
                      backgroundColor: logisticsEventTypesData[notification.eventType].iconBackgroundColor,
                      "& svg": { fontSize: 22 },
                    }}
                  >
                    {logisticsEventTypesData[notification.eventType].icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" pb={0.5} fontWeight={700} fontSize={12}>
                      {notification.title}
                    </Typography>
                    <Typography variant="body2" color="#64748B" fontSize={12}>
                      {notification.description}
                    </Typography>
                    <Tooltip
                      title={format(new Date(notification.occurredAt), "EE, MMMM d, yyyy 'at' h:mm aaa zzzz")}
                      arrow
                    >
                      <Typography variant="caption" color="text.disabled" sx={{ display: "inline-block", mt: 0.75 }}>
                        {formatDistanceToNow(new Date(notification.occurredAt), { addSuffix: true })}
                      </Typography>
                    </Tooltip>
                  </Box>
                </Box>
                {index < logisticsEvents.length - 1 && <Divider />}
              </Box>
            );
          })}
          {isLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!isLoading && logisticsEvents.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 3 }}>
              No notifications yet
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
};

const VIEW_OPTIONS: { value: LogisticsPickupsView; label: string; icon: ReactNode }[] = [
  { value: "table", label: "Table", icon: <TableRowsOutlined sx={{ fontSize: 18 }} /> },
  { value: "pipeline", label: "Pipeline", icon: <ViewKanbanOutlined sx={{ fontSize: 18 }} /> },
  { value: "calendar", label: "Calendar", icon: <CalendarMonthOutlined sx={{ fontSize: 18 }} /> },
];

export default function NewLogisticsPickups() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => readLogisticsPlatformFilters(searchParams));
  const filtersRef = useRef(filters);
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const [search, setSearch] = useState(filters.search);

  const persistFilters = useCallback(
    (patch: Partial<LogisticsPlatformFilters>) => {
      const next = { ...filtersRef.current, ...patch };
      filtersRef.current = next;
      writeLogisticsPlatformFilters(next);
      setFilters(next);
      setSearchParams(logisticsPlatformFiltersToSearchParams(searchParamsRef.current, next), { replace: true });
    },
    [setSearchParams],
  );

  useEffect(() => {
    const nextParams = logisticsPlatformFiltersToSearchParams(searchParamsRef.current, filtersRef.current);
    if (nextParams.toString() === searchParamsRef.current.toString()) return;
    setSearchParams(nextParams, { replace: true });
  }, [setSearchParams]);

  const { logisticsTickets, isLoading, refetch } = useLogisticsTickets({
    search,
    pageSize: filters.view === "table" ? undefined : 500,
    enabled: filters.view !== "calendar",
  });
  const { vendors } = useVendors();
  const filteredLogisticsTickets = useMemo(
    () =>
      logisticsTickets.filter(
        (ticket) =>
          matchesCoordinatorFilter(ticket, filters.coordinatorFilter) &&
          matchesVendorFilter(ticket, filters.vendorFilter) &&
          matchesNeedsActionFilter(ticket, filters.actionFilter),
      ),
    [filters.actionFilter, filters.coordinatorFilter, filters.vendorFilter, logisticsTickets],
  );
  const pipelinePickups = useMemo(
    () => mapLogisticsTicketsToPipelinePickups(filteredLogisticsTickets),
    [filteredLogisticsTickets],
  );
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
      }, 300),
    [],
  );

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      persistFilters({ search: event.target.value });
      debouncedSetSearch(event.target.value);
    },
    [debouncedSetSearch, persistFilters],
  );

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const handleViewChange = (nextView: LogisticsPickupsView) => {
    persistFilters({ view: nextView });
  };

  return (
    <FullPagePaperLayout>
      <PageBreadcrumbs links={[{ label: "Logistics Platform" }, { label: "Pickups" }]} />
      <PageTitle>Pickups</PageTitle>
      <PageActions>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", justifyContent: "flex-end" }}>
          <NotificationsAction />
          <Button
            component={RouterLink}
            variant="contained"
            to="/logistics/schedule-pickup"
            startIcon={<Add />}
            sx={{ whiteSpace: "nowrap" }}
          >
            schedule a pickup
          </Button>
        </Box>
      </PageActions>
      <PagePaperPreamble>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            px: 0.5,
            py: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", minWidth: 0 }}>
            <TextField
              size="small"
              placeholder="Search order number, etc."
              value={filters.search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={filters.coordinatorFilter}
                onChange={(event) => persistFilters({ coordinatorFilter: event.target.value })}
                displayEmpty
                aria-label="Filter by coordinator"
                sx={{
                  backgroundColor: "background.paper",
                  fontSize: 14,
                }}
              >
                {COORDINATOR_FILTER_OPTIONS.map(({ id, name }) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={filters.vendorFilter}
                onChange={(event) => persistFilters({ vendorFilter: event.target.value })}
                displayEmpty
                aria-label="Filter by vendor"
                renderValue={(value) =>
                  vendors.find((vendor) => vendor.id.toString() === value)?.name ??
                  (value === "unassigned" ? "No vendor" : "All vendors")
                }
                sx={{
                  backgroundColor: "background.paper",
                  fontSize: 14,
                }}
              >
                <MenuItem value="all">All vendors</MenuItem>
                {vendors.map((vendor) => (
                  <MenuItem key={vendor.id} value={vendor.id.toString()}>
                    {vendor.name}
                  </MenuItem>
                ))}
                <MenuItem value="unassigned">No vendor</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={filters.actionFilter}
                onChange={(event) => persistFilters({ actionFilter: event.target.value as ActionFilter })}
                displayEmpty
                aria-label="Filter by needs action"
                sx={{
                  backgroundColor: "background.paper",
                  fontSize: 14,
                }}
              >
                <MenuItem value="all">All tickets</MenuItem>
                <MenuItem value="needs_action">Needs Action</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <ButtonGroup
            variant="outlined"
            size="small"
            aria-label="pickups view"
            sx={{
              borderRadius: 1.5,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
              "& .MuiButtonGroup-grouped": {
                minWidth: 0,
                border: "none",
                borderRight: "1px solid",
                borderColor: "divider",
                borderRadius: "0 !important",
                "&:last-of-type": { borderRight: "none" },
              },
              "& .MuiButton-root": {
                textTransform: "none",
                px: 1.75,
                py: 0.75,
                fontWeight: 600,
                fontSize: 13,
                color: "text.secondary",
                backgroundColor: "#fff",
                gap: 0.75,
                "&:hover": {
                  backgroundColor: "#F8FAFC",
                },
                "& .MuiButton-startIcon": {
                  margin: 0,
                },
              },
            }}
          >
            {VIEW_OPTIONS.map((option) => {
              const selected = filters.view === option.value;
              return (
                <Button
                  key={option.value}
                  startIcon={option.icon}
                  onClick={() => handleViewChange(option.value)}
                  sx={
                    selected
                      ? {
                          backgroundColor: "#0089bd !important",
                          color: "#fff !important",
                          borderColor: "primary.main !important",
                          "&:hover": {
                            backgroundColor: "primary.dark",
                          },
                        }
                      : undefined
                  }
                >
                  {option.label}
                </Button>
              );
            })}
          </ButtonGroup>
        </Box>
      </PagePaperPreamble>

      {filters.view === "calendar" ? (
        <LogisticsTicketsCalendarView
          search={search}
          coordinatorFilter={filters.coordinatorFilter}
          vendorFilter={filters.vendorFilter}
        />
      ) : isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          {filters.view === "table" && <LogisticsTicketsTableView logisticsTickets={filteredLogisticsTickets} />}
          {filters.view === "pipeline" && (
            <LogisticsPickupPipelineView filteredPickups={pipelinePickups} onAdvanced={refetch} />
          )}
        </>
      )}
    </FullPagePaperLayout>
  );
}
